import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Panel from '@swf/components/Panel/Panel';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import Funds from '../../../../assets/icons/withdraw.svg';
import type { HelpSection } from '../../../../types/Cashier';
import { CardType } from '../../../../types/cardType';
import { getSavedMethod } from '../../../../utils/cashier';
import BonusInput from '../../../BonusOffer/components/BonusInput/BonusInput';
import Container from '../../../Container/Container';
import ForceMakeFastDepositCheckbox from '../../../SavePaymentInfo/components/ForceMakeFastDespositCheckbox/ForceMakeFastDepositCheckbox';
import ForceSaveDetailsCheckboxes from '../../../SavePaymentInfo/components/ForceSaveDetailsCheckboxes/ForceSaveDetailsCheckboxes';
import SavedCardCheckboxes from '../../../SavePaymentInfo/components/SavedCardCheckboxes/SavedCardCheckboxes';
import { alwaysMakeFastDepositMethods, alwaysSaveMethods, noInputSaved } from '../../data';
import HelpSectionContent from '../DepositAmount/components/HelpSectionContent/HelpSectionContent';

import {
  TXTCLI_Do_you_have_a_bonus_codeX,
  TXTWCL_Automatic_currency_conversions,
  TXTWCL_Available_balance,
  TXTWCL_Deposit,
  TXTWCL_Enter_bonus_code,
  TXTWCL_How_much_would_you_like_to_dep_ELL,
  TXTWCL_How_much_would_you_like_to_dep_ELL_1,
  TXTWCL_Please_tell_us_the_value_of_yo_ELL,
  TXTWCL_Please_tell_us_the_value_of_yo_ELL_2,
  TXTWCL_Please_tell_us_the_value_of_yo_ELL_3,
  TXTWCL_This_is_the_amount_you_paid_wh_ELL
} from './DepositAmount.messages.json';
import {
  bonusInput,
  checkbox,
  fundsAmountBalance,
  fundsAmountContainer,
  fundsIcon,
  panel,
  panelContent
} from './DepositAmount.scss';
import AmountCTAButton from './components/AmountCTAButton/AmountCTAButton';
import AmountContainer from './components/AmountContainer/AmountContainer';
import DepositFee from './components/DepositFee/DepositFee';

const setTitle = (type: CardType, license: LicenceId, firstName?: string) => {
  const titles: Partial<Record<CardType, TranslationTag>> = {
    [CardType.PStarsTopUp]: TXTWCL_Please_tell_us_the_value_of_yo_ELL,
    [CardType.Ricarica]:
      license === LicenceId.SPAIN
        ? TXTWCL_Please_tell_us_the_value_of_yo_ELL_3
        : TXTWCL_Please_tell_us_the_value_of_yo_ELL_2
  };

  return titles[type] || firstName
    ? TXTWCL_How_much_would_you_like_to_dep_ELL_1
    : TXTWCL_How_much_would_you_like_to_dep_ELL;
};

const DepositAmount: React.FC = () => {
  const {
    data: {
      savedCardInfoId,
      savedCards,
      paySystemDetails: { type }
    }
  } = useWizardContext();

  const isReloader = useSelector(select.account.isReloader);
  const license = useSelector(select.account.getLicense);
  const SwcReloaderBonus = useFeature(FeatureFlag.SwcReloaderBonus);
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const firstName = useSelector(select.account.getFirstName);
  const site = useSelector(select.cms.getSiteId);
  const isSiteUK = site === Sites.UK;
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const totalBalance = useSelector(select.account.getTotalBalance);
  const currency = useSelector(select.currency.getSelfLimitCurrency);
  const isSwcHideFxInfoEnabled = useFeature(FeatureFlag.SwcHideFxInfo);
  const isManageDepositLimitsEnabled = useFeature(FeatureFlag.SwcManageDepositLimits);
  const isPromoBanCoolOffPeriodActive = useSelector(select.account.isPromoBanCoolOffPeriodActive);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const currentlyTakingBreak = isSelfExcluded || nationalExclusion;
  const isDeOasisBonusEligibilityAllowed = useSelector(
    select.account.isDeOasisBonusEligibilityAllowed
  );

  const showSavedCardCheckboxes = useMemo((): boolean => {
    if (type === CardType.LuxonPay) {
      return false; // may be set as preferred method by accepting the BillingAgreement on LuxonPay processor site
    }
    if (savedCards?.length && savedCardInfoId) {
      const [{ savedCard }] = getSavedMethod(savedCards, savedCardInfoId);
      return savedCard && !savedCard.fastDeposit && noInputSaved.includes(savedCard.paySystem);
    }
    return false;
  }, [savedCardInfoId, savedCards, type]);

  const forceSaveDetails = useMemo(
    () => !showSavedCardCheckboxes && alwaysSaveMethods.includes(type),
    [showSavedCardCheckboxes, type]
  );

  const forceMakeFastDeposit = useMemo(() => alwaysMakeFastDepositMethods.includes(type), [type]);

  const helpSection: HelpSection = {
    title: TXTWCL_Automatic_currency_conversions,
    body: <HelpSectionContent />
  };
  const isBonusCodeAvailable = !hideBonusCode && isReloader && SwcReloaderBonus;

  const showBonusOffer = () => {
    switch (site) {
      case Sites.SPA:
        return isBonusCodeAvailable && !currentlyTakingBreak && !isPromoBanCoolOffPeriodActive;
      case Sites.DE:
        return isDeOasisBonusEligibilityAllowed && isReloader && SwcReloaderBonus;
      default:
        return isBonusCodeAvailable;
    }
  };

  return (
    <Container
      page={DepositAmount}
      headerProps={{
        title: <Translation {...TXTWCL_Deposit} />
      }}
      pageTopTitle={
        <>
          {isSiteUK && isMobileEmbedded && (
            <Panel className={panel}>
              <div className={panelContent}>
                <Funds className={fundsIcon} />
                <div className={fundsAmountContainer}>
                  <div className={fundsAmountBalance}>
                    <strong>
                      <CurrencyAmount value={Number(totalBalance ?? 0)} currency={currency} />
                    </strong>
                  </div>
                  <Translation tagName="span" {...TXTWCL_Available_balance} />
                </div>
              </div>
            </Panel>
          )}
          <Translation {...setTitle(type, license, firstName)} values={{ 0: firstName }} />
        </>
      }
      pageTopSubtitle={
        type === CardType.PStarsTopUp && (
          <Translation {...TXTWCL_This_is_the_amount_you_paid_wh_ELL} />
        )
      }
      helpSupportResolver
      showFooter
      limitsSection={isManageDepositLimitsEnabled}
      {...(!isSwcHideFxInfoEnabled && { helpSection })}
    >
      <fieldset>
        <AmountContainer />
        {showSavedCardCheckboxes && (
          <div className={checkbox}>
            <SavedCardCheckboxes />
          </div>
        )}
        {forceSaveDetails && <ForceSaveDetailsCheckboxes />}
        {forceMakeFastDeposit && <ForceMakeFastDepositCheckbox />}

        {showBonusOffer() && (
          <BonusInput
            selected
            label={TXTCLI_Do_you_have_a_bonus_codeX}
            placeholder={TXTWCL_Enter_bonus_code}
            className={bonusInput}
            checkBonusResponse
          />
        )}
      </fieldset>
      <AmountCTAButton />
      <DepositFee />
    </Container>
  );
};

DepositAmount.displayName = 'DepositAmount';

export default DepositAmount;
